"use client";

import { ACTIVE_LOCALES, type Locale, locale as localeDefinition } from "@tovi/shared";
import { DropdownMenu } from "./dropdown-menu.tsx";

/**
 * The language menu, shared by every surface that offers one.
 *
 * It lives here rather than in the back-office shell because the login screens
 * sit outside that shell and the till has no shell at all — and a merchant who
 * cannot read the sign-in page cannot get as far as the switcher inside.
 *
 * **Persistence is the caller's.** The web apps write the `tovi-locale` cookie
 * and reload, because the locale is resolved on the server and `<html lang>`
 * has to be right from the first frame. The till has neither a server nor a
 * cookie and simply re-renders. Baking either one in here would make the
 * component wrong for the other.
 */
export function LocaleSwitch({
  current,
  onSelect,
  label,
}: {
  current: Locale;
  onSelect: (next: Locale) => void;
  /**
   * The control's accessible name, and its only one: the trigger is the globe
   * and nothing else. App callers pass `t(...)`.
   */
  label: string;
}) {
  return (
    <DropdownMenu
      label={label}
      icon="globe"
      items={ACTIVE_LOCALES.map((code) => ({
        kind: "item" as const,
        key: code,
        label: localeDefinition(code).name,
        icon: code === current ? ("check" as const) : undefined,
        onSelect: () => onSelect(code),
      }))}
    />
  );
}
